import { IssueTypeDto } from '@issuemanager/api-client';
import {
  ExtendedAutocomplete,
  mergePaginatedDataAndValue,
  sortOptionsBySelected,
} from '@issuemanager/apps/client/components';
import {
  MAX_CHARACTERS_TO_SEARCH,
  truncateSearchText,
} from '@issuemanager/apps/client/utils';
import { useLocalizedIssueTypes } from '@issuemanager/client/features/issues';
import { IssueType } from '@issuemanager/constants';
import { useState, type JSX } from 'react';
import {
  useInfiniteIssueTypes,
  type UseIssueTypesParamsObject,
} from '../../../../features/issue-types';
import type { MultipleSelectorProps } from '../types';
import { useMultipleSelectorProps } from '../use-multiple-selector';

export type IssueTypeMultipleSelectorProps = MultipleSelectorProps<
  IssueTypeDto[],
  IssueTypeDto
> &
  Pick<UseIssueTypesParamsObject, 'filterByIssueTypeManager'>;

export function IssueTypeMultipleSelector(
  props: IssueTypeMultipleSelectorProps,
): JSX.Element {
  const { value, onChange, filterByIssueTypeManager, ...rest } = props;

  const [filterByText, setFilterByText] = useState<string>('');
  const [partialValue, setPartialValue] = useState(value ?? []);

  const localizedIssueTypes = useLocalizedIssueTypes();
  const issueTypesQuery = useInfiniteIssueTypes({
    params: {
      sortByName: 'asc',
      filterByName: filterByText,
      filterByEnabled: true,
      filterByIssueTypeManager,
    },
    handleError: true,
  });

  const options = mergePaginatedDataAndValue(
    issueTypesQuery.data?.pages.map((c) => c.data) ?? [],
    value ?? [],
  );

  const autocompleteProps = useMultipleSelectorProps({
    options,
    setInputValue: (text) => setFilterByText(truncateSearchText(text)),
    query: issueTypesQuery,
    value,
    setValue: onChange,
    partialValue,
    setPartialValue,
  });

  return (
    <ExtendedAutocomplete
      {...autocompleteProps}
      options={sortOptionsBySelected(
        options,
        partialValue,
        autocompleteProps.isOptionEqualToValue,
      )}
      getOptionLabel={
        (option) => localizedIssueTypes[option.name as IssueType] ?? option.name
        // eslint-disable-next-line react/jsx-curly-newline
      }
      maxLength={MAX_CHARACTERS_TO_SEARCH}
      {...rest}
    />
  );
}
